import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Button, ButtonAdd, Cell, Row, Status, StatusPayment, Text, TooltipIcon, TooltipWrapper } from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { PermissionGate } from 'screens/Security';
import { EmployeeActionRequiredType, EmployeeRole, EmployeeStatus, EmployeeType, IAlert, PayoutStatus } from 'types';

export const UserNames = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  word-break: break-word;

  .react-loading-skeleton {
    margin: ${toRem(2)} 0;
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    align-items: center;
    text-align: center;
  }

  h1 {
    color: ${theme.color.white};
    font-size: ${toRem(40)};
    font-weight: 300;
  }
`;

export const RealName = styled(Text)`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  margin-top: ${toRem(3.75)};
  color: ${theme.color.white};
`;

// Top Info Section
export const PersonalInfoLayout = styled.div`
  display: grid;
  width: 100%;
  grid-gap: ${toRem(11)};
  grid-template-columns: repeat(2, 1fr);
  padding: ${toRem(22)} ${toRem(22)} ${toRem(5.5)} ${toRem(22)};

  @media (min-width: ${theme.breakpoint.small}px) {
    grid-template-columns: repeat(auto-fit, minmax(${toRem(200)}, 1fr));
    grid-gap: ${toRem(16.5)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(22)};
    padding-bottom: ${toRem(8.75)};
  }

  > * {
    margin-bottom: 0;
  }
`;

// Tables
export const TableContainer = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: auto;
`;

export const EmptyContainer = styled.div`
  justify-content: center;
  align-items: center;
  width: 100%;
  height: auto;
  padding: ${toRem(66)} 0;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: end;

  > * {
    margin-left: ${toRem(11)};

    &:first-child {
      margin-left: 0;
    }
  }
`;

// Sites Table Rows
interface ISiteRowProps {
  isLoading?: boolean;
  userType: EmployeeType;
  role: string;
  site: string;
  jobTitle: string;
  disbursementMethod: string;
  employeeID?: string;
  status?: string;
  showEdit?: boolean;
  dismissReason?: string;
  onEdit?: () => void;
}

export const SiteRowAdmin: React.FC<ISiteRowProps> = ({ isLoading, userType, role, site, jobTitle, employeeID, disbursementMethod, onEdit, showEdit }) => {
  const displayButton = isLoading ? (
    <Skeleton width={100} />
  ) : (
    <PermissionGate requiredRoles={[EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN]}>
      <ActionButtons>{showEdit && <Button appearance="secondary" size="small" label="Edit" onClick={onEdit} data-cy="edit-site-button" />}</ActionButtons>
    </PermissionGate>
  );
  const displayRole = userType === EmployeeType.ADMIN ? 'Org Admin' : role;
  return (
    <Row data-cy="site-row">
      <Cell align="left" data-cy="site-cell">
        {isLoading ? <Skeleton width={100} /> : site}
      </Cell>
      <Cell align="left" data-cy="role-cell">
        {isLoading ? <Skeleton width={100} /> : displayRole}
      </Cell>
      <Cell align="left" data-cy="job-cell">
        {isLoading ? <Skeleton width={100} /> : jobTitle}
      </Cell>
      <Cell align="left" data-cy="employee-id-cell">
        {isLoading ? <Skeleton width={100} /> : employeeID}
      </Cell>
      <Cell align="left" data-cy="disbursement-cell">
        {isLoading ? <Skeleton width={100} /> : disbursementMethod}
      </Cell>
      <Cell align="right">{onEdit && displayButton}</Cell>
    </Row>
  );
};

export const SiteRow: React.FC<ISiteRowProps> = ({ isLoading, userType, role, site, jobTitle, employeeID, disbursementMethod, onEdit, showEdit, status, dismissReason }) => {
  const statusDisplay = (status: string, actionRequired?: string): string => {
    if (status === EmployeeStatus.ACTION_REQUIRED && actionRequired === EmployeeActionRequiredType.INVITE_EXPIRED) {
      return 'Expired';
    }
    if (status === 'invited') {
      return 'Invited';
    }
    const newStatus = status[0].toUpperCase() + status.substring(1);
    return newStatus;
  };

  const statusColor = (status: string, actionRequired?: string): Appearance => {
    if (status === EmployeeStatus.APPROVED) {
      return Appearance.SUCCESS;
    }
    if (status === EmployeeStatus.DISMISSED) {
      return Appearance.NEUTRAL;
    }
    if (status === EmployeeStatus.ACTION_REQUIRED && actionRequired === EmployeeActionRequiredType.INVITE_EXPIRED) {
      return Appearance.DANGER;
    }
    return Appearance.WARNING;
  };

  const displayButton = isLoading ? (
    <Skeleton width={100} />
  ) : (
    <PermissionGate requiredRoles={[EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN]}>
      <ActionButtons>{showEdit && <Button appearance="secondary" size="small" label="Edit" onClick={onEdit} data-cy="edit-site-button" />}</ActionButtons>
    </PermissionGate>
  );
  const displayRole = userType === EmployeeType.ADMIN ? 'Org Admin' : role;

  return (
    <Row bgColor="transparent" data-cy="site-row">
      <Cell align="left" data-cy="site-cell">
        {isLoading ? <Skeleton width={400} /> : site}
      </Cell>
      <Cell align="left" data-cy="role-cell">
        {isLoading ? <Skeleton width={100} /> : displayRole}
      </Cell>
      <Cell align="left" data-cy="job-cell">
        {isLoading ? <Skeleton width={100} /> : jobTitle}
      </Cell>
      <Cell align="left" data-cy="employee-id-cell">
        {isLoading ? <Skeleton width={100} /> : employeeID}
      </Cell>
      <Cell align="left" data-cy="disbursement-cell">
        {isLoading ? <Skeleton width={100} /> : disbursementMethod}
      </Cell>
      <Cell align="left" data-cy="status-cell">
        {isLoading ? (
          <Skeleton width={100} />
        ) : status ? (
          <div>
            <Status appearance={statusColor(status)} label={statusDisplay(status)} isLoading={isLoading} />
            {status === 'dismissed' && dismissReason && (
              <TooltipIcon id={`tooltip-dismissed-${site}`}>
                <p>
                  <strong>Dismiss Reason</strong>
                  <br />
                  {dismissReason}
                </p>
              </TooltipIcon>
            )}
          </div>
        ) : (
          '--'
        )}
      </Cell>
      <Cell align="right">{isLoading ? <Skeleton width={100} /> : onEdit && displayButton}</Cell>
    </Row>
  );
};

export const StyledSection = styled.section`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding-bottom: 60px;
`;

export const CenteredButtonAdd = styled(ButtonAdd)`
  margin-left: auto;
  margin-right: auto;
`;

export const ShorterEmptyContainer = styled(EmptyContainer)`
  padding-bottom: 25px;
`;

// Payout Row
interface IUserPayoutRowProps {
  amount: string;
  alert?: IAlert;
  businessDay: string;
  dateSubmitted: string;
  isLoading?: boolean;
  onClick: () => void;
  payoutId: string;
  sentBy: string;
  site?: string;
  status: PayoutStatus;
  timeSubmitted: string;
}

const StyledSkeleton = styled(Skeleton)`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  width: 100%;
  text-align: right;

  span {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    text-align: right;
  }
`;

export const UserPayoutRow = ({ alert, amount, businessDay, dateSubmitted, isLoading, onClick, payoutId, sentBy, site, status, timeSubmitted, ...rest }: IUserPayoutRowProps) => (
  <Row clickable onClick={onClick} {...rest}>
    <Cell minWidth={110} data-cy="business-day-cell">
      <TooltipWrapper>
        {isLoading ? (
          <Skeleton width={110} />
        ) : (
          <>
            {businessDay}
            <TooltipIcon iconMarginLeft={5.5} id={`user_payout_${payoutId}`}>
              <p>
                <strong>Date Submitted</strong>
                <br />
                {dateSubmitted}
                <br />
                {timeSubmitted}
              </p>
            </TooltipIcon>
          </>
        )}
      </TooltipWrapper>
    </Cell>

    <Cell minWidth={110} align="right" data-cy="tip-amount-cell">
      {isLoading ? <Skeleton width={110} /> : <strong>{amount}</strong>}
    </Cell>

    <Cell minWidth={110} data-cy="status-cell">
      <StatusPayment
        isLoading={isLoading}
        status={status}
        paymentId={payoutId}
        {...(alert && {
          subStatusLabel: alert.label,
          subStatusTooltip: alert.message,
        })}
      />
    </Cell>

    <Cell minWidth={154} data-cy="site-cell">
      {isLoading ? <Skeleton width={154} /> : site}
    </Cell>

    <Cell minWidth={110} data-cy="created-by-cell">
      {isLoading ? <Skeleton width={110} /> : sentBy}
    </Cell>

    <Cell minWidth={110} align="right" data-cy="payout-id-cell">
      {isLoading ? <StyledSkeleton width={110} /> : payoutId}
    </Cell>
  </Row>
);
